import { useMemo, useState } from "react";
import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";

import { CircularLoading } from "../../components/common";
import { ScreenMobile } from "../../components/common/ScreenMobile";
import { ResponsiveGenericTable } from "../../components/GenericTable/ResponsiveGenericTable";
import { useSessionContext } from "../../contexts/SessionContext";
import { DashViewAllButton } from "../home/components/PatientDash/components/DashViewAllButton";
import { AccessHistoryActions } from "./AccessHistoryActions";
import {
  AccessHistorySegmentedTabs,
  AccessHistoryTab,
  AccessHistoryTabHelperText,
  TAB_TITLES,
} from "./AccessHistorySegmentedTabs";
import {
  AccessHistorySearchBar,
  filterAccessHistoryBySearch,
} from "./AccessHistorySearchBar";
import {
  ACCESS_HISTORY_PREVIEW_LIMIT,
  AccessHistoryRow,
  useAccessHistoryData,
} from "./useAccessHistoryData";

export interface AccessHistoryContentProps {
  /**
   * preview: até 5 itens por aba + botão Ver tudo (mobile).
   * full: lista completa (vertical no mobile, tabela no desktop).
   */
  variant?: "preview" | "full";
  /** Controle externo do ScreenMobile "Ver tudo" */
  allListOpen?: boolean;
  onAllListOpenChange?: (open: boolean) => void;
  hideVerTudoButton?: boolean;
}

const generalColumns = [
  { column: "CPF", rowName: "identifier" },
  { column: "Nome", rowName: "name" },
  { column: "Organização", rowName: "organization" },
  { column: "Motivo", rowName: "purposeOfUse" },
  { column: "Data", rowName: "date" },
  { column: "Hora", rowName: "hour" },
];

const myAccessColumns = [
  { column: "CPF", rowName: "identifier" },
  { column: "Nome", rowName: "name" },
  { column: "Data", rowName: "date" },
  { column: "Hora", rowName: "hour" },
];

const docsColumns = [
  { column: "Documento clínico", rowName: "docType" },
  { column: "Organização", rowName: "organization" },
  { column: "Data", rowName: "date" },
  { column: "Hora", rowName: "hour" },
];

function filterRowsByTab(
  tab: AccessHistoryTab,
  data: AccessHistoryRow[],
  username: string
) {
  switch (tab) {
    case "1":
      return data.filter(
        (el) => el.identifier !== username && !el.identifier.includes("-")
      );
    case "2":
      return data.filter((el) => el.identifier === username);
    case "3":
      return data.filter(
        (el) => el.identifier !== username && el.identifier.includes("-")
      );
    default:
      return data;
  }
}

function AccessHistoryTabPanels({
  isPreview,
  isMobileLayout,
  rowsByTab,
}: {
  isPreview: boolean;
  isMobileLayout: boolean;
  rowsByTab: Record<AccessHistoryTab, AccessHistoryRow[]>;
}) {
  const mobileLayout =
    isMobileLayout && !isPreview ? "vertical" : "carousel";

  const limitRows = (rows: AccessHistoryRow[]) =>
    isPreview ? rows.slice(0, ACCESS_HISTORY_PREVIEW_LIMIT) : rows;

  const renderTab = (
    tab: AccessHistoryTab,
    columns: typeof generalColumns,
    rows: AccessHistoryRow[],
    withActions = false
  ) => (
    <TabPanel value={tab} sx={{ marginTop: "0px !important", p: 0 }}>
      <ResponsiveGenericTable
        tableColumns={columns}
        tableRows={limitRows(rows)}
        mobileLayout={isMobileLayout ? mobileLayout : undefined}
        actions={
          withActions
            ? (rowData: AccessHistoryRow) => (
                <AccessHistoryActions row={rowData} />
              )
            : undefined
        }
      />
    </TabPanel>
  );

  return (
    <>
      {renderTab("1", generalColumns, rowsByTab["1"])}
      {renderTab("2", myAccessColumns, rowsByTab["2"])}
      {renderTab("3", docsColumns, rowsByTab["3"], true)}
    </>
  );
}

/**
 * Conteúdo compartilhado do histórico de acessos — dashboard (ScreenMobile) e rota /history.
 */
export function AccessHistoryContent({
  variant = "full",
  allListOpen: externalAllListOpen,
  onAllListOpenChange,
  hideVerTudoButton = false,
}: AccessHistoryContentProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSessionContext();
  const [selectedTab, setSelectedTab] = useState<AccessHistoryTab>("1");
  const [internalAllListOpen, setInternalAllListOpen] = useState(false);
  const [fullListTab, setFullListTab] = useState<AccessHistoryTab>("1");

  const isPreview = variant === "preview";
  const allListOpen = externalAllListOpen ?? internalAllListOpen;
  const setAllListOpen = onAllListOpenChange ?? setInternalAllListOpen;

  const { data, loading } = useAccessHistoryData({ enabled: true });

  const rowsByTab = useMemo(
    () => ({
      "1": filterRowsByTab("1", data, user?.username ?? ""),
      "2": filterRowsByTab("2", data, user?.username ?? ""),
      "3": filterRowsByTab("3", data, user?.username ?? ""),
    }),
    [data, user?.username]
  );

  const activeTabRows = rowsByTab[selectedTab];
  const showVerTudoButton =
    isPreview &&
    isMobileLayout &&
    !hideVerTudoButton &&
    activeTabRows.length > ACCESS_HISTORY_PREVIEW_LIMIT;

  const tabsBlock = (
    <TabContext value={selectedTab}>
      <AccessHistorySegmentedTabs
        value={selectedTab}
        onChange={setSelectedTab}
      />

      <AccessHistoryTabHelperText tab={selectedTab} />

      <AccessHistoryTabPanels
        isPreview={isPreview}
        isMobileLayout={isMobileLayout}
        rowsByTab={rowsByTab}
      />

      {showVerTudoButton ? (
        <DashViewAllButton
          onClick={() => {
            setFullListTab(selectedTab);
            setAllListOpen(true);
          }}
          count={activeTabRows.length}
        />
      ) : null}
    </TabContext>
  );

  return (
    <>
      <Stack
        sx={{
          width: "100%",
          pb: isMobileLayout ? 1 : 0,
          px: isMobileLayout ? 1.5 : 0,
        }}
        spacing={isMobileLayout ? 1.5 : 0}
      >
        {loading ? (
          <Stack py={3} alignItems="center">
            <CircularLoading />
          </Stack>
        ) : (
          tabsBlock
        )}
      </Stack>

      {isPreview ? (
        <ScreenMobile
          open={allListOpen}
          onClose={() => setAllListOpen(false)}
          title={TAB_TITLES[fullListTab]}
        >
          {allListOpen ? (
            <AccessHistoryFullList initialTab={fullListTab} />
          ) : null}
        </ScreenMobile>
      ) : null}
    </>
  );
}

/** Lista completa em vertical — ScreenMobile "Ver tudo". */
function AccessHistoryFullList({
  initialTab,
}: {
  initialTab: AccessHistoryTab;
}) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSessionContext();
  const [selectedTab, setSelectedTab] = useState<AccessHistoryTab>(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const { data, loading } = useAccessHistoryData({ enabled: true });

  const rowsByTab = useMemo(
    () => ({
      "1": filterRowsByTab("1", data, user?.username ?? ""),
      "2": filterRowsByTab("2", data, user?.username ?? ""),
      "3": filterRowsByTab("3", data, user?.username ?? ""),
    }),
    [data, user?.username]
  );

  const filteredRowsByTab = useMemo(
    () => ({
      "1": filterAccessHistoryBySearch(rowsByTab["1"], searchQuery),
      "2": filterAccessHistoryBySearch(rowsByTab["2"], searchQuery),
      "3": filterAccessHistoryBySearch(rowsByTab["3"], searchQuery),
    }),
    [rowsByTab, searchQuery]
  );

  if (loading) {
    return (
      <Stack py={3} alignItems="center">
        <CircularLoading />
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: "100%", pb: 1, px: 1.5 }} spacing={1.5}>
      <TabContext value={selectedTab}>
        <AccessHistorySegmentedTabs
          value={selectedTab}
          onChange={setSelectedTab}
          compact
        />

        <AccessHistoryTabHelperText tab={selectedTab} compact />

        <AccessHistorySearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          resultCount={filteredRowsByTab[selectedTab].length}
        />

        <AccessHistoryTabPanels
          isPreview={false}
          isMobileLayout={isMobileLayout}
          rowsByTab={filteredRowsByTab}
        />
      </TabContext>
    </Stack>
  );
}
